/**
 * DangoteFinancing — Capital Deployment Decision View
 * Route: /corporate/financing
 * Role: "How do we fund it?" — Bankability assessment per structured project.
 * Input: Opportunities sent from Carbon Structuring.
 */
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDangote } from "@/context/DangoteContext";
import { PageHeader, SectionCard, MetricCard } from "@/components/ds";
import { RefreshCw, CheckCircle2, ArrowRight, TrendingDown, DollarSign, Info } from "lucide-react";
import client from "@/api/client";

const BANKABILITY_CONFIG = {
  Strong:      { color: "bg-emerald-100 text-emerald-700 border-emerald-200", bar: "bg-emerald-500" },
  Conditional: { color: "bg-amber-100 text-amber-700 border-amber-200",       bar: "bg-amber-400" },
  Weak:        { color: "bg-red-100 text-red-700 border-red-200",             bar: "bg-red-400" },
};

const STATUS_COLORS = {
  financing_review: "bg-purple-100 text-purple-700 border-purple-200",
  financing_ready:  "bg-emerald-100 text-emerald-700 border-emerald-200",
  executed:         "bg-[#0F172A] text-white border-transparent",
};
const STATUS_LABELS = {
  financing_review: "Under Review",
  financing_ready:  "Finance Ready",
  executed:         "Executed",
};

function ProjectCard({ opp, onTransition, transitioning }) {
  const bank = BANKABILITY_CONFIG[opp.bankability] || BANKABILITY_CONFIG.Weak;
  const canApprove = opp.status === "financing_review";
  const canExecute = opp.status === "financing_ready";

  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden bg-white"
      data-testid={`financing-card-${opp.opp_id}`}>
      {/* Header */}
      <div className="px-5 py-4 border-b border-slate-100">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full border ${bank.color}`}
                data-testid={`bankability-${opp.opp_id}`}>
                {opp.bankability} Bankability
              </span>
              <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full border ${STATUS_COLORS[opp.status]||"bg-slate-100 text-slate-500 border-slate-200"}`}>
                {STATUS_LABELS[opp.status]||opp.status}
              </span>
            </div>
            <p className="text-sm font-bold text-slate-900">{opp.name}</p>
            <p className="text-xs text-slate-400">{opp.bu_name} · {opp.asset_id} · {(opp.pathway||"internal").toUpperCase()}</p>
          </div>
          {/* Composite score */}
          <div className="text-right flex-shrink-0">
            <p className="text-lg font-bold text-teal-700">{opp.composite_score}</p>
            <p className="text-[9px] text-slate-400">score / 100</p>
          </div>
        </div>
      </div>

      <div className="px-5 py-4 space-y-4">
        {/* A. Bankable Financials */}
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
          <p className="text-xs font-bold text-emerald-700 uppercase tracking-wide mb-2">A. Bankable Financials</p>
          <p className="text-[9px] text-emerald-600 italic mb-3">Energy savings + circularity value — no validation dependency</p>
          <div className="grid grid-cols-2 gap-2 text-xs">
            {[
              ["CAPEX",         `$${Number((opp.capex_usd||0)/1e6).toFixed(1)}M`],
              ["Annual Savings",`$${Number((opp.cost_savings_usd_yr||0)/1e6).toFixed(2)}M/yr`],
              ["Circularity",   `$${Number((opp.circularity_value_usd_yr||0)/1e6).toFixed(2)}M/yr`],
              ["Total Bankable",`$${Number((opp.guaranteed_value_usd_yr||0)/1e6).toFixed(1)}M/yr`],
              ["Payback",       `${opp.payback_years||"—"}yr`],
              ["IRR",           `${opp.irr_pct||"—"}%`],
            ].map(([l,v]) => (
              <div key={l} className="bg-white/60 rounded-lg px-2.5 py-2">
                <p className="text-emerald-600">{l}</p>
                <p className="font-bold text-emerald-800">{v}</p>
              </div>
            ))}
          </div>
          {/* DSCR */}
          {opp.dscr && (
            <div className={`mt-2 flex items-center justify-between px-3 py-2 rounded-lg ${
              opp.dscr >= 1.25 ? "bg-emerald-100" : opp.dscr >= 1.0 ? "bg-amber-50" : "bg-red-50"
            }`}>
              <p className="text-xs font-semibold text-slate-700">DSCR (est.)</p>
              <div className="text-right">
                <p className={`text-sm font-bold ${opp.dscr >= 1.25 ? "text-emerald-700" : opp.dscr >= 1.0 ? "text-amber-700" : "text-red-700"}`}>
                  {opp.dscr}x
                </p>
                <p className="text-[9px] text-slate-400">DFI min: 1.25x · {opp.dscr >= 1.25 ? "PASSES" : opp.dscr >= 1.0 ? "MARGINAL" : "BELOW"}</p>
              </div>
            </div>
          )}
        </div>

        {/* B. Conditional Carbon Revenue */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <p className="text-xs font-bold text-blue-700 uppercase tracking-wide mb-2">B. Conditional Carbon Value</p>
          <p className="text-[9px] text-blue-600 italic mb-2">Not for base DSCR — subject to validation and issuance</p>
          <div className="grid grid-cols-3 gap-2 text-xs">
            {[
              ["Low  $15/t", `$${Number((opp.carbon_rev_low_usd_yr||0)/1e6).toFixed(1)}M/yr`],
              ["Base $25/t", `$${Number((opp.carbon_rev_base_usd_yr||0)/1e6).toFixed(1)}M/yr`],
              ["High $40/t", `$${Number((opp.carbon_rev_high_usd_yr||0)/1e6).toFixed(1)}M/yr`],
            ].map(([l,v]) => (
              <div key={l} className="bg-white/60 rounded-lg px-2 py-1.5">
                <p className="text-blue-500">{l}</p>
                <p className="font-bold text-blue-700">{v}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Data lineage */}
        {opp.calculation_id && (
          <div className="bg-slate-50 border border-slate-100 rounded-lg px-3 py-2 text-[9px] text-slate-400 flex flex-wrap gap-3">
            <span>Calc: <code className="text-teal-600 font-mono">{opp.calculation_id}</code></span>
            <span>Baseline: {opp.baseline_type}</span>
            <span>SDGs: {(opp.sdg_tags||[]).map(s=>`SDG ${s}`).join(", ") || "—"}</span>
            <span>Period: {opp.period_id}</span>
          </div>
        )}

        {/* Actions */}
        {canApprove && (
          <button onClick={() => onTransition(opp.opp_id, "financing_ready")}
            disabled={transitioning}
            className="w-full flex items-center justify-center gap-2 py-2.5 bg-teal-600 hover:bg-teal-700 text-white text-xs font-bold rounded-xl disabled:opacity-50"
            data-testid={`approve-financing-${opp.opp_id}`}>
            {transitioning ? <RefreshCw size={11} className="animate-spin"/> : <CheckCircle2 size={11}/>}
            Approve for Financing
          </button>
        )}
        {canExecute && (
          <button onClick={() => onTransition(opp.opp_id, "executed")}
            disabled={transitioning}
            className="w-full flex items-center justify-center gap-2 py-2.5 bg-[#0F172A] text-white text-xs font-bold rounded-xl hover:bg-slate-800 disabled:opacity-50"
            data-testid={`ready-execution-${opp.opp_id}`}>
            {transitioning ? <RefreshCw size={11} className="animate-spin"/> : <ArrowRight size={11}/>}
            Ready for Execution
          </button>
        )}
        {opp.status === "executed" && (
          <div className="flex items-center justify-center gap-2 text-xs text-emerald-700 font-bold py-2">
            <CheckCircle2 size={13}/> Executed
          </div>
        )}
      </div>
    </div>
  );
}

export default function DangoteFinancing() {
  const navigate     = useNavigate();
  const { selectedPeriod, period, vbCache, vbLoading, invalidateVb } = useDangote();
  const [transitioning, setTrans] = useState(false);

  const vbData  = selectedPeriod ? (vbCache[selectedPeriod] || null) : null;
  const loading = vbLoading && !vbData;

  const handleTransition = async (opp_id, newStatus) => {
    setTrans(true);
    await client.post(`/dangote/opportunities/${opp_id}/transition`, { status: newStatus });
    invalidateVb(selectedPeriod);
    setTrans(false);
  };

  const allOpps   = vbData?.opportunities || [];
  const portfolio = vbData?.portfolio     || {};
  const byStatus  = vbData?.by_status     || {};

  // Show financing-stage items, fall back to all if none
  const financeOpps = allOpps.filter(o => ["financing_review","financing_ready","executed"].includes(o.status));
  const displayedOpps = financeOpps.length > 0 ? financeOpps : [];

  // Portfolio-level financials (all structuring+ items)
  const pipelineOpps = allOpps.filter(o => !["identified","deprioritized"].includes(o.status));
  const totalCapex    = pipelineOpps.reduce((s,o) => s + (o.capex_usd||0), 0);
  const totalSavings  = pipelineOpps.reduce((s,o) => s + (o.cost_savings_usd_yr||0), 0);
  const totalCarbon   = pipelineOpps.reduce((s,o) => s + (o.carbon_rev_base_usd_yr||0), 0);
  const blendedPayback = totalSavings > 0 ? (totalCapex / totalSavings).toFixed(1) : "—";
  const strongCount   = financeOpps.filter(o => o.bankability === "Strong").length;
  const condCount     = financeOpps.filter(o => o.bankability === "Conditional").length;

  if (loading && !vbData) return (
    <div className="p-6 space-y-4">{[1,2,3].map(i=><div key={i} className="h-14 bg-slate-100 rounded-xl animate-pulse"/>)}</div>
  );

  return (
    <div className="p-6 lg:p-8 space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-0.5">Dangote Industries</p>
          <PageHeader title="Financing" />
          <p className="text-xs text-slate-400 mt-0.5">
            Capital deployment decision · {period?.label} · {financeOpps.length} projects in financing pipeline
          </p>
        </div>
      </div>

      {/* Portfolio summary */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <MetricCard label="Total CAPEX"      value={`$${Number(totalCapex/1e6).toFixed(0)}M`} color="text-slate-900" />
        <MetricCard label="Annual Savings"   value={`$${Number(totalSavings/1e6).toFixed(0)}M`} unit="/yr" color="text-teal-700" />
        <MetricCard label="Blended Payback"  value={`${blendedPayback}yr`} color="text-slate-900" />
        <MetricCard label="Carbon Rev (Base)" value={`$${Number(totalCarbon/1e6).toFixed(0)}M`} unit="/yr (conditional)" color="text-blue-700" />
        <MetricCard label="In Financing"     value={financeOpps.length} unit={`${strongCount} Strong · ${condCount} Cond.`} color="text-purple-700" />
      </div>

      {/* Value split */}
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-center justify-between">
          <div>
            <p className="text-xs font-bold text-emerald-700 uppercase tracking-wide">A. Bankable Value</p>
            <p className="text-xs text-emerald-600">Savings + circularity — DFI-grade</p>
          </div>
          <p className="text-xl font-bold text-emerald-800">${Number(totalSavings/1e6).toFixed(0)}M/yr</p>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-center justify-between">
          <div>
            <p className="text-xs font-bold text-blue-700 uppercase tracking-wide">B. Conditional Carbon</p>
            <p className="text-xs text-blue-600">Requires validation + issuance</p>
          </div>
          <p className="text-xl font-bold text-blue-800">${Number(totalCarbon/1e6).toFixed(0)}M/yr</p>
        </div>
      </div>

      {/* Projects */}
      {displayedOpps.length === 0 ? (
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-8 text-center">
          <DollarSign size={20} className="text-slate-400 mx-auto mb-2"/>
          <p className="text-slate-500 text-sm">No projects in financing pipeline</p>
          <button onClick={() => navigate("/corporate/carbon-structuring")}
            className="mt-3 text-xs text-teal-600 underline">Go to Carbon Structuring →</button>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {displayedOpps.map(o => (
            <ProjectCard key={o.opp_id} opp={o}
              onTransition={handleTransition} transitioning={transitioning} />
          ))}
        </div>
      )}

      {/* DSCR Disclaimer */}
      <div className="bg-red-50 border-l-4 border-red-500 rounded-xl px-4 py-3">
        <p className="text-xs font-bold text-red-700 mb-1">DSCR Calculation Notice</p>
        <p className="text-xs text-red-700 font-semibold">
          Carbon value excluded from DSCR until validated and issued.
        </p>
        <p className="text-xs text-red-600 mt-1">
          DSCR is calculated using Operational Value (Section A: energy savings + circularity) only.
          Section B (Indicative Carbon Value) must NOT be included in DSCR until VVB validation
          and carbon credit issuance are confirmed.
        </p>
      </div>

      {/* Disclaimer */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
        <p className="text-xs text-amber-700 italic">
          Validation-Ready Financial Model. CAPEX, IRR, payback, and DSCR are based on modeled assumptions.
          Subject to independent technical and financial due diligence.
          Operational value reflects modeled cost savings and circularity benefits.
          Indicative Carbon Value is conditional and subject to validation, issuance, and market conditions.
        </p>
      </div>
    </div>
  );
}
